<template>
    <div
        v-if="media.length > 0"
        ref="slider"
        class="flickity"
    >
        <div
            class="flickity__item"
            v-for="(item, index) in media"
            :key="index"
        >
            <img
                v-if="item.type === 'image'"
                width="512"
                height="349"
                :src="item.src"
                alt=""
                class="bsdc:size-full bsdc:object-cover"
            />
            <div
                v-if="item.type === 'video'"
                class="video-wrap"
            >
                <div
                    v-html="item.src"
                    class="video-wrap__inner"
                ></div>
                <span class="video-wrap__icon">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        class="size-6"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </span>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, onMounted } from 'vue';

    import Flickity from 'flickity';
    import 'flickity-imagesloaded';

    const props = defineProps({
        imageUrl: String,
        imageUrls: Array,
        videoEmbed: String,
        settings: Object,
        prevButtonIcon: {
            type: String,
            default: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6"><path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 0 1 0-1.06l7.5-7.5a.75.75 0 1 1 1.06 1.06L9.31 12l6.97 6.97a.75.75 0 1 1-1.06 1.06l-7.5-7.5Z" clip-rule="evenodd" /></svg>'
        },
        nextButtonIcon: {
            type: String,
            default: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="size-6"><path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 0 1 0 1.06l-7.5 7.5a.75.75 0 0 1-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 0 1 1.06-1.06l7.5 7.5Z" clip-rule="evenodd" /></svg>'
        },
    });

    const slider = ref(null);

    const media = computed(() => {
        const items = [];

        if (props.imageUrl) {
            items.push({ type: 'image', src: props.imageUrl });
        }
        if (props.imageUrls) {
            props.imageUrls.forEach(url => items.push({ type: 'image', src: url }));
        }
        if (props.videoEmbed) {
            items.push({ type: 'video', src: props.videoEmbed });
        }
        return items;
    });

    onMounted(() => {
        if (slider.value) {
            new Flickity(slider.value, {
                ...{
                    imagesLoaded: true,
                    wrapAround: true,
                    adaptiveHeight: true,
                    pageDots: false,
                    on: {
                        ready: function () {
                            if (this.prevButton) {
                                this.prevButton.element.innerHTML = props.prevButtonIcon;
                            }
                            if (this.nextButton) {
                                this.nextButton.element.innerHTML = props.nextButtonIcon;
                            }
                        },
                    },
                },
                ...props.settings,
            });
        }
    });
</script>

<style>
    @reference "../../css/app.css";

    .bsdc-config {

        /* Flickity */
        .flickity-enabled {
            @apply bsdc:relative;
        }

        .flickity-enabled.is-draggable {
            @apply bsdc:select-none;
        }

        .flickity-enabled.is-draggable .flickity-viewport {
            @apply bsdc:cursor-grab;
        }

        .flickity-enabled.is-draggable .flickity-viewport.is-pointer-down {
            @apply bsdc:cursor-grabbing;
        }

        .flickity-viewport {
            @apply bsdc:overflow-hidden bsdc:relative bsdc:h-full bsdc:touch-pan-y;
        }

        .flickity-slider {
            @apply bsdc:absolute bsdc:left-0 bsdc:w-full bsdc:h-full;
        }

        .flickity-button {
            @apply bsdc:size-[42px] bsdc:flex bsdc:items-center bsdc:justify-center bsdc:absolute bsdc:bottom-2 bsdc:bg-white bsdc:rounded-full;
            @apply bsdc:hover:bg-white/75 bsdc:transition-colors;

            svg {
                @apply bsdc:size-4;
            }

            &.previous {
                @apply bsdc:left-2;
            }

            &.next {
                @apply bsdc:right-2;
            }
        }

        .flickity-button[disabled] {
            @apply bsdc:opacity-0 bsdc:pointer-events-none;
        }

        .flickity__item {
            @apply bsdc:aspect-square bsdc:absolute bsdc:left-0 bsdc:w-full bsdc:rounded-md bsdc:overflow-hidden;

            img {
                @apply bsdc:w-full bsdc:block bsdc:rounded-md;
            }
        }

        /* Embedded videos */
        .video-wrap {
            @apply bsdc:relative;
        }

        .video-wrap__inner {
            @apply bsdc:relative bsdc:aspect-square;
        }

        .video-wrap__icon {
            @apply bsdc:size-6 bsdc:flex bsdc:items-center bsdc:justify-center bsdc:absolute bsdc:bottom-3 bsdc:right-3 bsdc:pointer-events-none bsdc:rounded-full bsdc:bg-black;

            svg {
                @apply bsdc:w-3 bsdc:text-white;
            }
        }

        .flickity-viewport {
            @apply bsdc:transition-[height] bsdc:ease-in-out bsdc:duration-300;
        }

        .video-wrap {
            iframe {
                @apply bsdc:size-full bsdc:absolute bsdc:top-0 bsdc:left-0;
            }
        }
    }
</style>
